import React, {useCallback, useRef, useState} from 'react';

import {DropArea, DropAreaProps} from '@ELib/components/DropArea';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';
import {Awaitable} from '@ELib/utilities/types';

const containsFiles = (types: readonly string[]): boolean =>
  types.every((type) => type === 'Files');

const hasAcceptingTypes = (
  keys: string[] | undefined,
  items: DataTransferItemList
): boolean => {
  if (!keys || keys.length === 0) {
    return true;
  } else {
    return [...items]
      .map((item) => item.type)
      .every((type) => keys.includes(type));
  }
};

function isFileSystemEntry(
  entry: FileSystemEntry | null
): entry is FileSystemEntry {
  return entry !== null;
}

interface DropCallbacks {
  onDragEnter: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
}

export interface UseDropReturnType {
  dropArea: React.ReactElement;
  dropCallbacks: DropCallbacks;
  isDragging: boolean;
}

export interface UseDropProps
  extends Omit<Omit<DropAreaProps, 'isDragging'>, 'content'> {
  onlyAcceptingFiles?: boolean;
  check?: (arg0: Record<string, string>) => boolean;
  content?: React.ReactNode;
  /** An optional list of data transfer type keys which the dragged item must contain so that it can be dropped. */
  dataKeys?: string[];
  fileKeys?: string[];
  onDropAccept?: (
    data: Record<string, string>,
    files: FileList,
    entries: FileSystemEntry[]
  ) => Awaitable<void>;
}

export const useDrop = ({
  onlyAcceptingFiles,
  check,
  content,
  dataKeys,
  fileKeys,
  onDropAccept,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  ...props
}: UseDropProps): UseDropReturnType => {
  const dragEnterCounter = useRef(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (
        (onlyAcceptingFiles &&
          containsFiles(event.dataTransfer.types) &&
          hasAcceptingTypes(fileKeys, event.dataTransfer.items)) ||
        (!onlyAcceptingFiles &&
          (dataKeys?.every((key) => event.dataTransfer.types.includes(key)) ||
            hasAcceptingTypes(fileKeys, event.dataTransfer.items)))
      ) {
        dragEnterCounter.current += 1;
        setIsDragging(true);
      }

      onDragEnter?.(event);
    },
    [dataKeys, fileKeys, onDragEnter, onlyAcceptingFiles]
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      if (
        (onlyAcceptingFiles &&
          containsFiles(event.dataTransfer.types) &&
          hasAcceptingTypes(fileKeys, event.dataTransfer.items)) ||
        (!onlyAcceptingFiles &&
          (dataKeys?.every((key) => event.dataTransfer.types.includes(key)) ||
            hasAcceptingTypes(fileKeys, event.dataTransfer.items)))
      ) {
        dragEnterCounter.current -= 1;

        if (dragEnterCounter.current === 0) {
          setIsDragging(false);
        }
      }

      onDragLeave?.(event);
    },
    [dataKeys, fileKeys, onDragLeave, onlyAcceptingFiles]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      onDragOver?.(event);
    },
    [onDragOver]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      const dataList = Object.fromEntries(
        event.dataTransfer.types.map((key) => [
          key,
          event.dataTransfer.getData(key),
        ])
      );

      if (
        onDropAccept &&
        (check == null || check(dataList)) &&
        ((onlyAcceptingFiles &&
          containsFiles(event.dataTransfer.types) &&
          hasAcceptingTypes(fileKeys, event.dataTransfer.items)) ||
          (!onlyAcceptingFiles &&
            (dataKeys?.every((key) => event.dataTransfer.types.includes(key)) ||
              hasAcceptingTypes(fileKeys, event.dataTransfer.items))))
      ) {
        const entries = [...event.dataTransfer.items]
          .map((it) => it.webkitGetAsEntry())
          .filter(isFileSystemEntry);

        onDropAccept(dataList, event.dataTransfer.files, entries);
      }

      dragEnterCounter.current = 0;
      setIsDragging(false);

      onDrop?.(event);
    },
    [check, dataKeys, fileKeys, onDrop, onDropAccept, onlyAcceptingFiles]
  );

  useMountEffect(() => {
    const dragOverCallback = (event: DragEvent): void => {
      event.preventDefault();
    };

    const dropCallback = (event: DragEvent): void => {
      event.preventDefault();
    };

    window.addEventListener('dragover', dragOverCallback, false);
    window.addEventListener('drop', dropCallback, false);

    return (): void => {
      window.removeEventListener('dragover', dragOverCallback);
      window.removeEventListener('drop', dropCallback);
    };
  });

  return {
    dropArea: (
      <DropArea
        data-testid='drop-area'
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        {...props}
      >
        {content}
      </DropArea>
    ),
    dropCallbacks: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    isDragging,
  };
};
